import { Button, Cell, DataTable, FormControl, Grid, Icon, TableFooter, Tooltip } from 'bold-ui'
import { CpfOrCns } from 'components/CpfOrCns'
import { CidadaoSelectField, CidadaoSelectFieldModel } from 'components/form'
import { TableBox } from 'components/table/TableBox'
import { usePagination } from 'components/table/usePagination'
import { css } from 'emotion'
import { Profissional } from 'graphql/types.generated'
import React from 'react'
import { MetaArray } from 'util/metaPath'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { EditableListForm, EditableListFormRenderProps } from 'view/atendimentos/detail/components/EditableListForm'
import { formatNomeCidadao } from 'view/cidadao/utils-cidadao'

import { CidadaoStatusGrupos } from './CidadaoStatusGrupos'
import { IdadeCidadaoGrupos } from './IdadeCidadaoGrupos'
import { cidadaoEditableTableValidator } from './validator-cidadaoEditableTable'

export interface CidadaoEditableTableModel {
  _id: ID
  cidadao: CidadaoSelectFieldModel & {
    faleceu?: boolean
  }
}

interface CidadaoEditableTableProps {
  readonly name: MetaArray<CidadaoEditableTableModel>
  readonly required?: boolean
  readonly profissionaisEnvolvidos?: Pick<Profissional, 'id' | 'cpf' | 'cns'>[]
}

export function CidadaoEditableTable(props: CidadaoEditableTableProps) {
  const { name, profissionaisEnvolvidos, required } = props

  const {
    handleSubmit,
    removeItem,
    input: { value },
  } = useEditableListField({ name })

  const allItems: CidadaoEditableTableModel[] = (value || []).slice().reverse()
  const { paginatedItems, tableProps } = usePagination({ items: allItems, initialPageSize: 5 })

  const renderForm = (renderProps: EditableListFormRenderProps<CidadaoEditableTableModel>) => {
    const { name, handleSubmit, values } = renderProps
    const hasCidadaoSelecionado = !!values?.cidadao

    return (
      <Grid gap={2} style={styles.tableHeader}>
        <Cell size={6}>
          <CidadaoSelectField
            name={name.cidadao}
            label='Cidadão'
            placeholder='Pesquise por nome, CPF ou CNS'
            required={required}
            ativo
            obito={false}
            selectedCidadaoIds={allItems?.length ? allItems.map((item) => item.cidadao?.id).filterNotFalsy() : []}
          />
        </Cell>

        <Cell size={2}>
          <FormControl label={<span>&nbsp;</span>}>
            <Tooltip text={!hasCidadaoSelecionado ? 'Informe um cidadão para adicionar à lista' : ''}>
              <Button
                kind='primary'
                disabled={!hasCidadaoSelecionado}
                onClick={handleSubmit}
                size='small'
                data-testid='Button.AdicionarParticipantes'
              >
                Adicionar
              </Button>
            </Tooltip>
          </FormControl>
        </Cell>
      </Grid>
    )
  }

  const renderButtons = (row: CidadaoEditableTableModel) => (
    <Tooltip text='Excluir'>
      <Button type='button' kind='normal' skin='ghost' size='small' onClick={() => removeItem(row)}>
        <Icon icon='trashOutline' />
      </Button>
    </Tooltip>
  )

  return (
    <TableBox
      header={
        <EditableListForm<CidadaoEditableTableModel>
          render={renderForm}
          validate={cidadaoEditableTableValidator(allItems, profissionaisEnvolvidos)}
          onSubmit={handleSubmit}
          prefix='new'
        />
      }
    >
      <DataTable<CidadaoEditableTableModel>
        rows={paginatedItems}
        columns={[
          {
            name: 'nome',
            header: 'Nome',
            render: (row) => formatNomeCidadao({ nome: row.cidadao.nome, nomeSocial: row.cidadao.nomeSocial }),
            style: styles.tableRowNome,
          },
          {
            name: 'cpfCns',
            header: 'CPF ou CNS',
            render: (row) => <CpfOrCns cpf={row.cidadao.cpf} cns={row.cidadao.cns} />,
            style: styles.tableRowCpfCns,
          },
          {
            name: 'dataNascimento',
            header: 'Data de nasc.',
            render: (row) => <IdadeCidadaoGrupos dataNascimento={row.cidadao.dataNascimento} />,
            style: styles.tableRowCommon,
          },
          {
            name: 'sexo',
            header: 'Sexo',
            render: (row) => row.cidadao.sexo?.titleCase() ?? '-',
            style: styles.tableRowCommon,
          },
          {
            name: 'status',
            header: '',
            render: (row) => <CidadaoStatusGrupos faleceu={row.cidadao.faleceu} unificado={row.cidadao.unificado} />,
            style: styles.tableRowStatus,
          },
          {
            name: 'buttons',
            render: renderButtons,
            style: styles.tableRowButtons,
          },
        ]}
      />
      <TableFooter style={styles.tableFooter} {...tableProps} />
    </TableBox>
  )
}

const styles = {
  tableHeader: css`
    padding-block: 0.5rem;
  `,
  tableFooter: css`
    border-top: 0;
  `,
  tableRowNome: css`
    width: 15rem;
    line-height: 1.5rem;
  `,
  tableRowCpfCns: css`
    width: 14rem;
    line-height: 1.5rem;
  `,
  tableRowCommon: css`
    width: 8rem;
    line-height: 1.5rem;
  `,
  tableRowStatus: css`
    width: 12rem;
    line-height: 1.5rem;
  `,
  tableRowButtons: css`
    width: 3.5rem;
    text-align: right;
  `,
}
